import { useState, useRef, useEffect, useCallback } from 'react'
import { FiChevronDown, FiBriefcase, FiCheck } from 'react-icons/fi'
import { scalekitAuthService, Organization } from '@/lib/scalekit.service'
import { useAuth } from '@/contexts/AuthContext'
import styles from './OrgSwitcher.module.css'

export default function OrgSwitcher() {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Load organizations on mount
  useEffect(() => {
    const loadOrganizations = async () => {
      setIsLoading(true)
      try {
        const orgs = await scalekitAuthService.listOrganizations()
        setOrganizations(orgs)
      } catch (error) {
        console.error('Failed to load organizations:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadOrganizations()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSwitchOrg = useCallback(
    async (orgId: string) => {
      if (orgId === user?.organization_id) {
        setIsOpen(false)
        return
      }

      setIsSwitching(true)
      try {
        const success = await scalekitAuthService.switchOrganization(orgId)
        if (success) {
          // Reload the page to refresh all data for the new org
          window.location.reload()
        }
      } catch (error) {
        console.error('Failed to switch organization:', error)
      } finally {
        setIsSwitching(false)
        setIsOpen(false)
      }
    },
    [user?.organization_id]
  )

  // Find current organization
  const currentOrg = organizations.find((org) => org.id === user?.organization_id)

  // Don't render if only one or no organizations
  if (organizations.length <= 1) {
    return null
  }

  return (
    <div className={styles.orgSwitcher} ref={dropdownRef}>
      <button
        className={styles.switcherButton}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading || isSwitching}
        aria-expanded={isOpen}
        type="button"
      >
        <FiBriefcase size={16} className={styles.orgIcon} />
        <span className={styles.orgName}>{currentOrg?.name || 'Select Organization'}</span>
        <FiChevronDown className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>Switch Organization</div>
          <div className={styles.dropdownList}>
            {organizations.map((org) => (
              <button
                key={org.id}
                className={`${styles.dropdownItem} ${org.id === user?.organization_id ? styles.active : ''}`}
                onClick={() => handleSwitchOrg(org.id)}
                disabled={isSwitching}
                type="button"
              >
                <span className={styles.orgItemName}>{org.name}</span>
                {org.id === user?.organization_id && <FiCheck size={16} className={styles.checkIcon} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
